/**
 * Settings card: Team Lead Access
 *
 * Lets the venue host:
 *   - generate a one-time 6-digit PIN that their staff (bartender / floor
 *     manager) types into the dedicated `/staff` mobile login
 *   - toggle team-lead access ON/OFF at any time (kills any live session)
 *   - see when the PIN was last rotated
 *
 * The PIN plaintext is shown EXACTLY ONCE — re-fetching just returns whether
 * a PIN is set, never the value. Host must rotate if they lose it.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { ShieldCheck, KeyRound, Copy, Loader2, RefreshCcw, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TeamLeadAccessCard = () => {
  const [status, setStatus] = useState(null);  // {enabled, pin_set, pin_set_at, venue_code}
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [revealedPin, setRevealedPin] = useState(null);  // shown only right after generation

  // Deep-link that the QR encodes — /dj-handoff/<VENUE_CODE>. When the
  // staff scans it, `useParams` in StaffPage pre-fills the venue code and
  // the login screen only asks for the 6-digit PIN. One-tap onboarding.
  const handoffUrl = useMemo(() => {
    if (!status?.venue_code) return '';
    return `${window.location.origin}/dj-handoff/${status.venue_code}`;
  }, [status?.venue_code]);

  const copyHandoffUrl = () => {
    if (!handoffUrl) return;
    navigator.clipboard.writeText(handoffUrl).catch(() => {});
    toast.success('Hand-off link copied');
  };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/team-lead/status`);
      setStatus(r.data);
    } catch (err) {
      console.warn('[TeamLead] fetchStatus failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleGeneratePin = async () => {
    if (!window.confirm(status?.pin_set
        ? 'Generate a NEW PIN? The current PIN will stop working immediately.'
        : 'Generate a fresh 6-digit PIN for your team lead?')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/team-lead/set-pin`);
      setRevealedPin(r.data.pin);
      await fetchStatus();
      toast.success('PIN generated. Copy it now — it cannot be retrieved later.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate PIN');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (enabled) => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/team-lead/toggle`, { enabled });
      await fetchStatus();
      toast.success(enabled ? 'Team lead access ENABLED' : 'Team lead access DISABLED');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const handleCopyPin = () => {
    if (!revealedPin) return;
    navigator.clipboard.writeText(revealedPin).then(
      () => toast.success('PIN copied'),
      () => toast.error('Could not copy'),
    );
  };

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl"
      data-testid="team-lead-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#FF007F]" />
          <h2 className="text-base font-bold">DJ Hand-off Access</h2>
        </div>
        {!loading && status && (
          <Switch
            data-testid="team-lead-toggle"
            checked={!!status.enabled}
            disabled={busy || !status.pin_set}
            onCheckedChange={handleToggle}
          />
        )}
      </div>

      <p className="text-sm text-white/60 mb-4 leading-relaxed">
        Hand your bar manager or floor host a QR code + 6-digit PIN. They land in a lean
        mobile app that lets them play/pause/skip, adjust volume, add tracks to the queue,
        and approve guest requests + shoutouts. They cannot touch settings, analytics, or
        playlists.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
        </div>
      ) : revealedPin ? (
        <div className="bg-[#FF007F]/10 border border-[#FF007F]/40 rounded-xl p-4 mb-4">
          <p className="text-xs text-white/60 mb-2">
            Share this PIN with your team lead now. It will not be shown again.
          </p>
          <div className="flex items-center gap-3">
            <code
              data-testid="team-lead-pin-value"
              className="text-3xl font-mono font-bold tracking-[0.4em] text-[#FF007F] flex-1"
            >
              {revealedPin}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyPin}
              data-testid="copy-pin-btn"
            >
              <Copy className="w-4 h-4 mr-2" />Copy
            </Button>
          </div>
          <p className="text-xs text-white/40 mt-3">
            Venue code: <span className="font-mono text-white/70">{status?.venue_code}</span>
            <span className="block mt-1">
              Hand-off link: <span className="font-mono text-white/70">{handoffUrl || `${window.location.origin}/dj-handoff`}</span>
            </span>
          </p>
        </div>
      ) : (
        <div className="mb-4 space-y-3">
          {status?.pin_set ? (
            <>
              <div className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3">
                <p className="text-sm">
                  <KeyRound className="w-4 h-4 inline mr-2 text-white/60" />
                  PIN is configured.{' '}
                  {status.pin_set_at && (
                    <span className="text-white/40 text-xs">
                      Last rotated{' '}
                      {new Date(status.pin_set_at).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}
                    </span>
                  )}
                </p>
              </div>

              {/* QR + link — staff scans on their phone → land on prefilled login */}
              {handoffUrl && (
                <div
                  className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex items-center gap-4"
                  data-testid="dj-handoff-qr-card"
                >
                  <div className="bg-white rounded-lg p-2 flex-shrink-0" data-testid="dj-handoff-qr">
                    <QRCodeSVG value={handoffUrl} size={96} bgColor="#ffffff" fgColor="#000000" level="M" includeMargin={false} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white/60 mb-1 flex items-center gap-1.5">
                      <QrCode className="w-3.5 h-3.5" /> Have your staff scan this
                    </p>
                    <p
                      className="text-xs font-mono text-white/70 truncate cursor-pointer hover:text-white"
                      onClick={copyHandoffUrl}
                      title="Tap to copy"
                      data-testid="dj-handoff-url"
                    >
                      {handoffUrl}
                    </p>
                    <button
                      type="button"
                      onClick={copyHandoffUrl}
                      className="mt-2 text-[10px] uppercase tracking-widest text-[#FF007F] font-semibold flex items-center gap-1 hover:text-white"
                      data-testid="dj-handoff-copy-link"
                    >
                      <Copy className="w-3 h-3" /> Copy link
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-white/40 italic">No PIN set yet.</p>
          )}
        </div>
      )}

      <Button
        onClick={handleGeneratePin}
        disabled={busy}
        className="bg-[#FF007F] hover:bg-[#FF007F]/90 text-white w-full"
        data-testid="generate-pin-btn"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : status?.pin_set ? (
          <RefreshCcw className="w-4 h-4 mr-2" />
        ) : (
          <KeyRound className="w-4 h-4 mr-2" />
        )}
        {status?.pin_set ? 'Rotate PIN' : 'Generate PIN'}
      </Button>
    </div>
  );
};

export default TeamLeadAccessCard;
